// crack-sequence.jsx - cinematic pre-recorded obfuscation/attack sequence.
// Replaces the live typing playground. Plays in the hero, auto-loops.

const { useState, useEffect, useRef } = React;

function CrackSequence({ running = true, onCycle }) {
  const [step, setStep] = useState(0);
  // steps: 0 plaintext visible → 1 scan line → 2 obfuscated → 3 attack attempts → 4 blocked
  useEffect(() => {
    if (!running) return;
    const timings = [2600, 1400, 2400, 3000, 2200];
    const t = setTimeout(() => {
      setStep((s) => {
        const n = (s + 1) % timings.length;
        if (n === 0) onCycle && onCycle();
        return n;
      });
    }, timings[step]);
    return () => clearTimeout(t);
  }, [step, running, onCycle]);

  return (
    <div style={{
      position: 'relative',
      background: 'rgba(10,10,15,0.6)',
      border: '1px solid rgba(147,197,253,0.15)',
      borderRadius: 14, overflow: 'hidden',
      backdropFilter: 'blur(18px)',
      boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(147,197,253,0.06)',
      fontFamily: 'JetBrains Mono, monospace',
    }}>
      <div style={{
        padding: '14px 22px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', borderBottom: '1px solid rgba(245,241,232,0.06)',
        fontSize: 11, color: 'rgba(245,241,232,0.5)', letterSpacing: 1.2, textTransform: 'uppercase',
      }}>
        <span style={{ display: 'flex', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: '#3a3a40' }} />
          <span style={{ width: 10, height: 10, borderRadius: 5, background: '#3a3a40' }} />
          <span style={{ width: 10, height: 10, borderRadius: 5, background: '#3a3a40' }} />
        </span>
        <span style={{ opacity: 0.7 }}>validator.py → validator.pyd</span>
        <span style={{ color: step >= 2 ? '#93c5fd' : 'rgba(245,241,232,0.4)', transition: 'color .6s' }}>
          {step < 2 ? 'scanning' : step < 4 ? 'sealed' : 'attack · blocked'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 280 }}>
        <SourcePane step={step} />
        <OutputPane step={step} />
      </div>

      <div style={{
        padding: '14px 22px', borderTop: '1px solid rgba(245,241,232,0.06)',
        display: 'flex', gap: 6, alignItems: 'center',
        fontSize: 10, color: 'rgba(245,241,232,0.45)', letterSpacing: 1.3, textTransform: 'uppercase',
      }}>
        {['encode', 'virtualize', 'seal', 'deflect', 'complete'].map((label, i) => (
          <React.Fragment key={label}>
            <span style={{
              padding: '5px 12px', borderRadius: 20,
              background: i === step ? 'rgba(59,130,246,0.18)' : 'transparent',
              color: i < step ? 'rgba(147,197,253,0.8)' : i === step ? '#f5f1e8' : 'rgba(245,241,232,0.3)',
              border: i === step ? '1px solid rgba(147,197,253,0.4)' : '1px solid transparent',
              transition: 'all .6s cubic-bezier(.2,.7,.2,1)',
            }}>{String(i + 1).padStart(2, '0')} · {label}</span>
            {i < 4 && <span style={{ flex: i === step || i + 1 === step ? 1 : 0, height: 1, background: 'rgba(147,197,253,0.2)', transition: 'flex .6s' }} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const SOURCE = [
  { t: 'def validate_license(key, machine):', k: ['def'] },
  { t: '    salt = "f9a2-internal-2026"', k: [] },
  { t: '    h = sha256(f"{key}:{machine}:{salt}")', k: [] },
  { t: '    r = requests.post(VERIFY_URL, json={"h": h})', k: [] },
  { t: '    return r.status_code == 200 and r.json()["ok"]', k: ['return', 'and'] },
];

function SourcePane({ step }) {
  // step 0: plaintext, step 1: scan, step 2+: faded & obfuscated overlay
  const fade = step >= 2 ? 0.1 : 1;
  return (
    <div style={{ position: 'relative', padding: '22px 22px', borderRight: '1px solid rgba(245,241,232,0.06)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 9, color: 'rgba(245,241,232,0.4)', letterSpacing: 1.2, textTransform: 'uppercase' }}>source</div>
      <pre style={{
        margin: 0, fontSize: 12, lineHeight: 1.7, color: 'rgba(245,241,232,0.85)',
        opacity: fade, transition: 'opacity 1.2s ease',
      }}>{SOURCE.map((l) => l.t).join('\n')}</pre>

      {/* scan line */}
      <div style={{
        position: 'absolute', left: 0, right: 0, height: 40,
        top: step === 1 ? '100%' : step === 0 ? '-10%' : '100%',
        background: 'linear-gradient(180deg, transparent, rgba(147,197,253,0.35), transparent)',
        transition: step === 1 ? 'top 1.4s cubic-bezier(.6,0,.4,1)' : 'top .3s',
        pointerEvents: 'none',
      }} />

      {/* obfuscated replacement */}
      {step >= 2 && (
        <pre style={{
          position: 'absolute', top: 22, left: 22, right: 22, margin: 0,
          fontSize: 11, lineHeight: 1.7, color: 'rgba(147,197,253,0.85)',
          opacity: step >= 2 ? 1 : 0,
          transition: 'opacity .9s ease .2s',
        }}>{`vm.op[0x4F2A] ← _b7a(_x9f2, _c31)
vm.const[0x01] ← _enc(0x2a,0x19,0x7f,...)
vm.op[0x4F2B] ← _h(0x${'a'}cb3, sha_veil)
vm.op[0x4F2C] ← _dispatch(_net, _p)
vm.op[0x4F2D] ← vm.return(_r.status)`}</pre>
      )}
    </div>
  );
}

function OutputPane({ step }) {
  return (
    <div style={{ position: 'relative', padding: '22px 22px', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 9, color: step >= 4 ? '#f87171' : 'rgba(147,197,253,0.8)', letterSpacing: 1.2, textTransform: 'uppercase', transition: 'color .6s' }}>
        {step < 3 ? 'sealed .pyd' : step < 4 ? 'attacker terminal' : 'terminated'}
      </div>

      {step < 3 && (
        <div style={{ opacity: step >= 2 ? 1 : 0, transition: 'opacity 1s ease' }}>
          <pre style={{ margin: 0, fontSize: 10, lineHeight: 1.7, color: 'rgba(147,197,253,0.55)' }}>
{`4d 5a 90 00 03 00 00 00  04 00 00 00 ff ff 00 00
b8 00 00 00 00 00 00 00  40 00 00 00 00 00 00 00
0e 1f ba 0e 00 b4 09 cd  21 b8 01 4c cd 21 54 68
76 6d 2e 73 65 61 6c 65  64 3a 20 70 79 76 6d 70
72 6f 74 65 63 74 2d 33  2e 31 33 2d 77 69 6e 78
36 34 2e 70 79 64 00 00  00 00 00 00 00 00 00 00`}
          </pre>
          <div style={{ marginTop: 14, fontSize: 11, color: '#93c5fd', opacity: 0.85 }}>
            ▸ 0 plaintext strings · 0 .pyc bytes · unique opcode map
          </div>
        </div>
      )}

      {step >= 3 && (
        <div style={{ fontSize: 11.5, lineHeight: 1.9, animation: 'co-fade .4s ease both' }}>
          <div style={{ color: 'rgba(245,241,232,0.5)' }}>$ strings validator.pyd | grep -i secret</div>
          <div style={{ color: 'rgba(245,241,232,0.7)', paddingLeft: 2 }}>(no output)</div>
          <div style={{ color: 'rgba(245,241,232,0.5)', marginTop: 6 }}>$ pylingual validator.pyd</div>
          <div style={{ color: '#fca5a5' }}>error: not a .pyc · cannot decompile</div>
          <div style={{ color: 'rgba(245,241,232,0.5)', marginTop: 6 }}>$ python -c "import validator; dis.dis(validator)"</div>
          <div style={{ color: '#fca5a5' }}>SystemError: VM integrity check failed</div>
          {step >= 4 && (
            <div style={{
              marginTop: 14, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
              color: '#fca5a5', fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase',
              animation: 'co-fade .5s ease both',
            }}>
              ▪ process terminated · anti-debug engaged
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes co-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}

window.CrackSequence = CrackSequence;
